import { useProductsPricing } from "@/queries/products";
import { formatPrice } from "@/utils/money";
import { resolveVariantTitle } from "@/utils/variant";
import { SalonStockerHttpTypes } from "@repo/api-sdk";
import { useMemo } from "react";

/** A single orderable variant row for the locked supplier. */
export interface VariantGridRow {
  variantId: string;
  /** The seller offer id for the locked supplier — used as the cart `offer_id`. */
  offerId: string;
  /** Display name of the variant (e.g. shade name). */
  name: string;
  /** Secondary code line (SKU / option values). */
  code: string;
  priceAmount: number;
  priceFormatted: string;
  isOutOfStock: boolean;
  /** The variant's own image URL, or null when it has none. */
  image: string | null;
}

export interface UseVariantGridDataReturn {
  rows: VariantGridRow[];
  inStockCount: number;
  isLoading: boolean;
  isError: boolean;
}

export interface UseVariantGridDataProps {
  productId: string;
  /** The supplier the dialog is locked to. */
  sellerId: string;
  enabled?: boolean;
}

/**
 * Loads fresh, customer-specific pricing for every variant of a product and
 * reduces it to the rows orderable from a single locked supplier.
 *
 * Variants with no offer from that supplier, or whose offer is `inactive`
 * (no longer on the marketplace), are excluded. `out_of_stock` variants are
 * kept but flagged so the UI can disable ordering them.
 */
export function useVariantGridData({
  productId,
  sellerId,
  enabled = true
}: UseVariantGridDataProps): UseVariantGridDataReturn {
  const { data, isLoading, isError } = useProductsPricing({
    product_ids: enabled && productId ? [productId] : []
  });

  const rows = useMemo<VariantGridRow[]>(() => {
    const product = data?.products?.find((p) => p.id === productId);
    if (!product) return [];

    const result: VariantGridRow[] = [];
    for (const variant of product.variants) {
      const offer = variant.seller_offers.find((o) => o.seller_id === sellerId);
      // Skip variants this supplier doesn't offer, or offers that are no
      // longer available on the marketplace.
      if (
        !offer ||
        offer.status === SalonStockerHttpTypes.StoreSellerOfferStatus.INACTIVE
      ) {
        continue;
      }

      const code = variant.options
        ?.map((option) => option.value)
        .filter(Boolean)
        .join(" · ");
      const codeOrSku = code || variant.sku;

      result.push({
        variantId: variant.id,
        offerId: offer.id,
        // A single-variant product carries Medusa's placeholder title, which
        // names nothing the buyer chose - fall through to whatever actually
        // identifies the row.
        name: resolveVariantTitle(variant.title) ?? codeOrSku,
        code: codeOrSku,
        priceAmount: offer.calculated_price.calculated_amount,
        priceFormatted: formatPrice(offer.calculated_price.calculated_amount),
        isOutOfStock:
          offer.status ===
          SalonStockerHttpTypes.StoreSellerOfferStatus.OUT_OF_STOCK,
        image: variant.image ?? null
      });
    }
    return result;
  }, [data, productId, sellerId]);

  const inStockCount = useMemo(
    () => rows.filter((row) => !row.isOutOfStock).length,
    [rows]
  );

  return { rows, inStockCount, isLoading, isError };
}
